import { useFormik } from "formik";
import * as Yup from "yup";
import { PersonDto } from "../../../../../../interfaces/schema";
import { getUpdatePersonDTO } from "./StamoplysningerTop";
import { Dispatch, SetStateAction } from "react";
import { UseMutateFunction } from "@tanstack/react-query";
import { cprMatchesBirthdate } from "../../../../../../util/validation";
import usePersonCprNumber from "../../../../../../hooks/usePersonCprNumber";
import { stripCprRestrictedChanges } from "../../../../../../util/cprAccess";

const initialValues = (person: PersonDto, cpr: string | undefined) => {
  const {
    bemaerkninger,
    efternavn,
    emailPrivat,
    foedselsDato,
    fornavne,
    koen,
    titel,
    titel1,
    tlfMobilNr,
    prioriterEmailPrivat,
  } = person;
  return {
    Bemaerkninger: bemaerkninger ?? "",
    CprLoebeNr: cpr ? cpr.split("-")[1] : undefined,
    Efternavn: efternavn ?? "",
    EmailPrivat: emailPrivat ?? "",
    FoedselsDato: foedselsDato ? new Date(foedselsDato) : undefined,
    Fornavne: fornavne ?? "",
    Koen: koen ?? "",
    TitelId: titel ? titel.id : "",
    Titel1Id: titel1 ? titel1.id : "",
    TlfMobilNr: tlfMobilNr ?? "",
    prioriterEmailPrivat: prioriterEmailPrivat,
  };
};

export const usePersonStamoFormik = (
  person: PersonDto,
  isLoading: boolean,
  setEditMode: Dispatch<SetStateAction<boolean>>,
  mutate: UseMutateFunction<unknown, unknown, UpdatePersonDTO, unknown>,
  toggleCpr: boolean,
) => {
  const { canReadCpr, cpr, cprLoading, invalidateCpr } = usePersonCprNumber(
    person.id,
    toggleCpr,
  );

  const formik = useFormik<IUpdatePersonFormik>({
    validationSchema: Yup.object().shape({
      EmailPrivat: Yup.string().email("E-mail skal være en gyldig e-mail"),
      FoedselsDato: Yup.date()
        .nullable()
        .max(new Date(), "Må ikke være i fremtiden"),
      CprLoebeNr: Yup.string()
        .matches(/^[0-9]+$/, "CPR-nummer skal kun indeholde tal")
        .min(4, "CPR-nummer skal være 4 cifre")
        .max(4, "CPR-nummer skal være 4 cifre")
        .test("cpr", "CPR matcher ikke fødselsdato", function (item) {
          return cprMatchesBirthdate(item, this.parent.FoedselsDato);
        }),
    }),
    initialValues: initialValues(person, cpr),
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!isLoading) {
        // ADV-349: uden CprRead sendes CprLoebeNr aldrig, og FoedselsDato ikke for en person
        // med CPR-nummer — backenden ville afvise hele PATCH'en.
        const changedValues: UpdatePersonDTO = stripCprRestrictedChanges(
          getUpdatePersonDTO(initialValues(person, cpr), values),
          {
            canReadCpr,
            harCprNummer: !!person.harCprNummer,
            foedselsDato: person.foedselsDato,
          },
        );
        if (Object.keys(changedValues).length === 0) setEditMode(false);
        else {
          if (
            !changedValues.FoedselsDato &&
            !person.foedselsDato &&
            changedValues.CprLoebeNr
          ) {
            delete changedValues.CprLoebeNr;
          }
          mutate(changedValues);
        }
      }
    },
  });
  return { formik, cprLoading, invalidateCpr, canReadCpr };
};
